
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "step-1---display-the-apply-with-seek-button"
    }}>{`Step 1 - Display the Apply with SEEK button`}</h1>
    <p>{`Apply with SEEK can be used by a candidate to pre-fill in two scenarios:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`When a candidate accesses your application form via a job advertisement on SEEK.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When a candidate accesses your application form from a different source or directly.`}</p>
      </li>
    </ol>
    <h2 {...{
      "id": "candidate-arrives-from-seek"
    }}>{`Candidate arrives from SEEK`}</h2>
    <p>{`If the candidate has arrived from a `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
      }}>{`link-out`}</a>{` advertisement on SEEK, you will receive the token in a `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter appended to the query string of the posted `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationMethod/field/applicationUri"
      }}><inlineCode parentName="a">{`applicationUri`}</inlineCode></a>{`, for example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text"
      }}>{`https://www.example.com/job/123445/?seek-token=93WyyF1h6eaeb58ccc384b0e8353daa3011f1ece
`}</code></pre>
    <p>{`The token should be stored on your system for use in the `}<inlineCode parentName="p">{`applyWithSEEKLink`}</inlineCode>{` query below or when submitting a Completion Signal to notify that an application has been completed within your recruitment software.
The token is unique to that specific candidate, job ad, and session, and should be stored only for the duration of the application process.`}</p>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.249170542d87168e08b81e352db223ebedb70ca1.mmd.svg')} title="Full sequence" />
    <h2 {...{
      "id": "candidate-arrives-from-another-source"
    }}>{`Candidate arrives from another source`}</h2>
    <p>{`Where a candidate has arrived at your application form from another source (direct link, company career site etc.), the `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter will not be present in the query string and should be omitted in the `}<inlineCode parentName="p">{`applyWithSEEKLink`}</inlineCode>{` query below.`}</p>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.25b42e0f55d845a95e860c5c56b0e95f55b381f7.mmd.svg')} title="Full sequence" />
    <h2 {...{
      "id": "retrieving-the-apply-with-seek-link"
    }}>{`Retrieving the Apply with SEEK link`}</h2>
    <p>{`The Apply with SEEK button displayed on your application page must link to a specific authorization URL defined by SEEK.
The linked authorization page will allow the candidate to authenticate and authorize access to their profile for pre-filling your application form.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`applyWithSeekButton`}</inlineCode>{` query returns the URL that must be used in your Apply with SEEK button to redirect the candidate to the SEEK authorization page.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-valid-graphql-operation\",\"value\":\"query ($input: _unstable_applyWithSeekButtonInput!) {\\n  _unstable_applyWithSeekButton(input: $input) {\\n    applyWithSeekButton {\\n      url\\n      imageUrl\\n      buttonLabel\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":58,\"column\":1,\"offset\":2786},\"end\":{\"line\":68,\"column\":4,\"offset\":3022},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    // A URI that the candidate will be redirected to after completing their authorization.\\n    // This URI must be supplied to SEEK in advance to ensure the security of candidate data.\\n    \\\"redirectUri\\\": \\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n    // Can contain any string and will be passed back as a param on the `redirectUri` after\\n    // authorization is complete.\\n    // `state` is typically used to rehydrate a state in your application when SEEK's\\n    // auhorization redirects back to the redirectUri and to protect against CSRF attacks.\\n    // Your software should validate the state that comes back as being as you expect.\\n    \\\"state\\\": \\\"AnyDataToBePassedBackAfterAuth\\\",\\n\\n    // The SEEK-allocated identifier for the advertiser, which enables the OAuth consent form to\\n    // display their name (i.e. “ATS on behalf of Advertiser”).\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // Applicable only for jobs that are listed on SEEK. It is the SEEK-allocated identifier for\\n    // the job opening.\\n    // If the role is not listed on SEEK, this field can be omitted.\\n    \\\"positionOpeningId\\\": \\\"globalPublicTest:positionOpening:positions:F65aT5rAv3ZSusuunFWT8i\\\",\\n\\n    // seek-token received as part of the query string when the candidate arrived from SEEK.\\n    // If no seek-token is available (ie. the candidate did not arrive from SEEK),\\n    // this field can be omitted.\\n    \\\"seek-token\\\": \\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\",\\n\\n    // Two-character ISO-639 locale for branding of the authorization page and button.\\n    // This is optional and will default to the hirer's country if omitted, or fallback\\n    // to \\\"au\\\" / SEEK branding if the country is not supported.\\n    // eg. A hirer based in Australia will display the \\\"Apply with SEEK\\\" branding,\\n    // while a hirer based in Singapore will display \\\"Apply With JobStreet\\\" branding.\\n    \\\"locale\\\": \\\"au\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":70,\"column\":1,\"offset\":3024},\"end\":{\"line\":106,\"column\":4,\"offset\":4979},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"url\\\": \\\"https://www.seek.com.au/api/iam/oauth2/authorize?client_id=xxxxxxx&redirect_uri=https://partner.example.com/jobad/00000001/apply&state=AnyDataToBePassedBackAfterAuth&hirer_id=seekAnzPublicTest:organization:seek:93WyyF1h&seek-token=eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ97&locale=au\\\",\\n    \\\"imageUrl\\\": \\\"https://www.seek.com.au/assets/images/apply-with-seek/awsk-logo.png\\\",\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":108,\"column\":1,\"offset\":4981},\"end\":{\"line\":116,\"column\":4,\"offset\":5464},\"indent\":[1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-valid-graphql-operation\",\"value\":\"query": true,
        "($input:": true,
        "_unstable_applyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "_unstable_applyWithSeekButton(input:": true,
        "$input)": true,
        "applyWithSeekButton": true,
        "url\\n": true,
        "imageUrl\\n": true,
        "buttonLabel\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":58,\"column\":1,\"offset\":2786},\"end\":{\"line\":68,\"column\":4,\"offset\":3022},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "A": true,
        "URI": true,
        "that": true,
        "the": true,
        "candidate": true,
        "will": true,
        "be": true,
        "redirected": true,
        "to": true,
        "after": true,
        "completing": true,
        "their": true,
        "authorization.\\n": true,
        "This": true,
        "must": true,
        "supplied": true,
        "SEEK": true,
        "in": true,
        "advance": true,
        "ensure": true,
        "security": true,
        "of": true,
        "data.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n": true,
        "Can": true,
        "contain": true,
        "any": true,
        "string": true,
        "and": true,
        "passed": true,
        "back": true,
        "as": true,
        "a": true,
        "param": true,
        "on": true,
        "`redirectUri`": true,
        "after\\n": true,
        "authorization": true,
        "is": true,
        "complete.\\n": true,
        "`state`": true,
        "typically": true,
        "used": true,
        "rehydrate": true,
        "state": true,
        "your": true,
        "application": true,
        "when": true,
        "SEEK's\\n": true,
        "auhorization": true,
        "redirects": true,
        "redirectUri": true,
        "protect": true,
        "against": true,
        "CSRF": true,
        "attacks.\\n": true,
        "Your": true,
        "software": true,
        "should": true,
        "validate": true,
        "comes": true,
        "being": true,
        "you": true,
        "expect.\\n": true,
        "\\\"state\\\":": true,
        "\\\"AnyDataToBePassedBackAfterAuth\\\",\\n\\n": true,
        "The": true,
        "SEEK-allocated": true,
        "identifier": true,
        "for": true,
        "advertiser,": true,
        "which": true,
        "enables": true,
        "OAuth": true,
        "consent": true,
        "form": true,
        "to\\n": true,
        "display": true,
        "name": true,
        "(i.e.": true,
        "“ATS": true,
        "behalf": true,
        "Advertiser”).\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "Applicable": true,
        "only": true,
        "jobs": true,
        "are": true,
        "listed": true,
        "SEEK.": true,
        "It": true,
        "for\\n": true,
        "job": true,
        "opening.\\n": true,
        "If": true,
        "role": true,
        "not": true,
        "SEEK,": true,
        "this": true,
        "field": true,
        "can": true,
        "omitted.\\n": true,
        "\\\"positionOpeningId\\\":": true,
        "\\\"globalPublicTest:positionOpening:positions:F65aT5rAv3ZSusuunFWT8i\\\",\\n\\n": true,
        "seek-token": true,
        "received": true,
        "part": true,
        "query": true,
        "arrived": true,
        "from": true,
        "SEEK.\\n": true,
        "no": true,
        "available": true,
        "(ie.": true,
        "did": true,
        "arrive": true,
        "SEEK),\\n": true,
        "\\\"seek-token\\\":": true,
        "\\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\",\\n\\n": true,
        "Two-character": true,
        "ISO-639": true,
        "locale": true,
        "branding": true,
        "page": true,
        "button.\\n": true,
        "optional": true,
        "default": true,
        "hirer's": true,
        "country": true,
        "if": true,
        "omitted,": true,
        "or": true,
        "fallback\\n": true,
        "\\\"au\\\"": true,
        "/": true,
        "supported.\\n": true,
        "eg.": true,
        "hirer": true,
        "based": true,
        "Australia": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\"": true,
        "branding,\\n": true,
        "while": true,
        "Singapore": true,
        "With": true,
        "JobStreet\\\"": true,
        "branding.\\n": true,
        "\\\"locale\\\":": true,
        "\\\"au\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":70,\"column\":1,\"offset\":3024},\"end\":{\"line\":106,\"column\":4,\"offset\":4979},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/api/iam/oauth2/authorize?client_id": "xxxxxxx&redirect_uri",
        "\\\"imageUrl\\\":": true,
        "\\\"https://www.seek.com.au/assets/images/apply-with-seek/awsk-logo.png\\\",\\n": true,
        "\\\"buttonLabel\\\":": true,
        "SEEK\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":108,\"column\":1,\"offset\":4981},\"end\":{\"line\":116,\"column\":4,\"offset\":5464},\"indent\":[1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: _unstable_applyWithSeekButtonInput!) {
  _unstable_applyWithSeekButton(input: $input) {
    applyWithSeekButton {
      url
      imageUrl
      buttonLabel
    }
  }
}
`}</code></pre>
    <p><em parentName="p">{` Note: SEEK is investigating providing an additional implementation for a SEEK provided button widget. `}</em></p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;